import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../api'
import { KeyRound, FileText, Trash2, PlayCircle, Inbox, Terminal, Puzzle, Target, ClipboardCheck, Server, HardDrive, Save, Check, MessageCircle, QrCode, ShieldAlert } from 'lucide-react'
import { Card, PageHeader, Button, Input, Textarea, Field, Alert, Modal, ModalHeader, EmptyState, Tabs } from '../components/ui'
import { isAdmin } from '../auth'

// Pengaturan aplikasi ini tersebar di beberapa halaman (bukan satu tabel/pola config tunggal —
// ambang redaman & tiket disimpan di app_config, bot/AI di bot_config di sisi bot, cleanup device
// juga app_config tapi beda key, dst). Halaman ini dulunya satu scroll panjang (Peta Pengaturan 9
// link + 5 kartu config berbaris) -- diaudit 2026-08-05 krn beberapa link ternyata cuma duplikat
// tujuan yg SUDAH ada di sidebar utama (Ambang Redaman->Tiket Gangguan, Bot WA->Pengaturan Bot,
// Kredensial ONT->Model ONT, Kontak Pelanggan->Daftar Pelanggan -- 4 dibuang dari sini), sementara
// 5 kartu config (termasuk toggle operasional penting spt Isolir Otomatis) sama sekali tidak
// terindeks, cuma ketemu kalau kebetulan scroll sampai paling bawah. Sekarang dikelompokkan per tab
// tema supaya tiap pengaturan kelihatan tanpa perlu diingat-ingat/scroll buta. Quick-link ke
// halaman kecil yg TETAP TIDAK py slot sidebar sendiri (Target Kerja/Template WA Tagihan/Midtrans/
// Script Automasi/Driver OLT -- lihat komentar Layout.jsx kenapa) didistribusi ke tab sesuai tema.
function QuickLink({ icon: Icon, label, to, desc }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div style={{ display: 'flex', gap: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>
        <Icon size={16} color="var(--brand)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 2 }}>{desc}</div>
        </div>
      </div>
    </Link>
  )
}

function QuickLinkGrid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 4 }}>{children}</div>
}

// Ganti Password + Template Laporan WA -- dipakai bareng di tab "Akun" (admin) & tampilan
// polos teknisi (tidak ada tab sama sekali, cuma 2 kartu ini yg relevan buat dia).
function AkunCards({ form, setForm, pesan, loading, onSubmit }) {
  return (
    <div style={{ maxWidth: 480 }}>
      <Card style={{ padding: 24, marginBottom: 20 }}>
        <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <KeyRound size={16} color="var(--brand)" /> Ganti Password
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>Ubah password akun dashboard</p>

        {pesan && <Alert type={pesan.type}>{pesan.text}</Alert>}

        <form onSubmit={onSubmit}>
          {[
            { key: 'password_lama', label: 'Password Lama' },
            { key: 'password_baru', label: 'Password Baru' },
            { key: 'konfirmasi', label: 'Konfirmasi Password Baru' },
          ].map(f => (
            <Field key={f.key} label={f.label} style={{ marginBottom: 14 }}>
              <Input type="password" value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
            </Field>
          ))}
          <Button type="submit" loading={loading}>{loading ? 'Menyimpan...' : 'Simpan Password'}</Button>
        </form>
      </Card>

      <Card style={{ padding: 24 }}>
        <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={16} color="var(--brand)" /> Template Laporan WA
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Format yang harus digunakan teknisi saat melapor di grup WhatsApp</p>
        <pre style={{
          background: 'var(--brand-soft)', border: '1px solid #d7dbfb', borderRadius: 'var(--radius-sm)',
          padding: 16, fontSize: 13, lineHeight: 1.8, color: 'var(--text)',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap'
        }}>
{`#LAPORAN
Nama      : Agus, Udin
Lokasi    : Jl. Merdeka No.12, RT.05
Pekerjaan : PSB
Detail    : Instalasi ODP ke rumah, kabel 30m
Status    : PROSES
Catatan   : Menunggu material`}
        </pre>
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
          <div>• <strong>Nama</strong>: bisa lebih dari satu, pisahkan dengan koma</div>
          <div>• <strong>Pekerjaan</strong>: gunakan kode (PSB, GNG, CAB, dll)</div>
          <div>• <strong>Status</strong>: hanya <strong>PROSES</strong> atau <strong>SELESAI</strong></div>
          <div>• <strong>Detail</strong> dan <strong>Catatan</strong>: boleh dikosongkan</div>
        </div>
      </Card>
    </div>
  )
}

export default function Pengaturan() {
  const [form, setForm] = useState({ password_lama: '', password_baru: '', konfirmasi: '' })
  const [pesan, setPesan] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState('akun')

  async function gantiPassword(e) {
    e.preventDefault()
    if (form.password_baru !== form.konfirmasi) {
      setPesan({ type: 'error', text: 'Password baru dan konfirmasi tidak sama' }); return
    }
    if (form.password_baru.length < 6) {
      setPesan({ type: 'error', text: 'Password minimal 6 karakter' }); return
    }
    setLoading(true)
    try {
      await api.post('/auth/ganti-password', {
        password_lama: form.password_lama,
        password_baru: form.password_baru
      })
      setPesan({ type: 'success', text: 'Password berhasil diubah!' })
      setForm({ password_lama: '', password_baru: '', konfirmasi: '' })
    } catch (e) {
      setPesan({ type: 'error', text: e.response?.data?.message || 'Gagal mengubah password' })
    } finally { setLoading(false) }
  }

  // Teknisi cuma butuh ganti password sendiri -- tab lain semuanya keputusan/config tingkat
  // admin, tidak relevan & tidak boleh diakses dari akun teknisi (halaman ini sengaja tetap 1
  // route shared, lihat Layout.jsx) -- makanya teknisi tidak lihat Tabs sama sekali, cuma
  // 2 kartu akun langsung (persis tampilan lama, tidak berubah).
  const admin = isAdmin()

  if (!admin) {
    return (
      <div>
        <PageHeader title="Pengaturan" subtitle="Konfigurasi akun" />
        <AkunCards form={form} setForm={setForm} pesan={pesan} loading={loading} onSubmit={gantiPassword} />
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="Pengaturan" subtitle="Konfigurasi akun dan sistem" />

      <Tabs
        tabs={[
          ['akun', 'Akun', KeyRound],
          ['operasional', 'Operasional', ClipboardCheck],
          ['billing', 'Billing & Isolir', ShieldAlert],
          ['integrasi', 'Integrasi', Server],
        ]}
        active={tab} onChange={setTab}
      />

      {tab === 'akun' && (
        <AkunCards form={form} setForm={setForm} pesan={pesan} loading={loading} onSubmit={gantiPassword} />
      )}

      {tab === 'operasional' && (
        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <AturanPegawaiCard />
          <FotoSizeThresholdCard />
          <CleanupConfigCard />
          <QuickLinkGrid>
            <QuickLink icon={Target} label="Target Kerja" to="/target-kerja" desc="Target jumlah pekerjaan bulanan global (dipakai Payroll)" />
          </QuickLinkGrid>
        </div>
      )}

      {tab === 'billing' && (
        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <IsolirPengaturanCard />
          <QuickLinkGrid>
            <QuickLink icon={MessageCircle} label="Template WA Tagihan" to="/pengaturan-tagihan-wa" desc="Isi pesan WA tagihan & info metode pembayaran" />
            <QuickLink icon={QrCode} label="Pengaturan Midtrans" to="/pengaturan-midtrans" desc="Server key & webhook URL untuk pembayaran online" />
          </QuickLinkGrid>
        </div>
      )}

      {tab === 'integrasi' && (
        <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <GenieAcsConfigCard />
          <QuickLinkGrid>
            <QuickLink icon={Terminal} label="Script Automasi" to="/script" desc="Jalankan automasi manual (mis. Sinkron ke GenieACS) tanpa lewat Perangkat Pelanggan" />
            <QuickLink icon={Puzzle} label="Driver OLT" to="/driver-olt" desc="Katalog vendor/driver OLT yang didukung aplikasi (read-only)" />
          </QuickLinkGrid>
        </div>
      )}
    </div>
  )
}

// Isolir Otomatis (2026-07-29) -- efek jaringan (limit bandwidth + captive portal, address-list
// Mikrotik "FAZNET-ISOLIR" terpisah dari MSISOLIR) DEFAULT MATI lewat isolir_network_enabled --
// nyalakan cuma setelah objek Mikrotik-nya dibuat & diuji manual (lihat isolirNetwork.js). Engine
// keputusan isolir/pulih otomatis sendiri (cron syncIsolirStatus) SELALU jalan terlepas dari toggle
// ini -- toggle ini cuma gerbang efek ke Mikrotik, bukan gerbang logic isolir-nya.
function IsolirPengaturanCard() {
  const [networkEnabled, setNetworkEnabled] = useState(false)
  const [bandwidthLimit, setBandwidthLimit] = useState('')
  const [popupIntervalJam, setPopupIntervalJam] = useState('')
  const [popupJudul, setPopupJudul] = useState('')
  const [popupPesan, setPopupPesan] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    const res = await api.get('/isolir-pengaturan')
    setNetworkEnabled(!!res.data.isolir_network_enabled)
    setBandwidthLimit(res.data.isolir_bandwidth_limit || '1M')
    setPopupIntervalJam(String(res.data.tagihan_popup_interval_jam ?? 6))
    setPopupJudul(res.data.tagihan_popup_judul || '')
    setPopupPesan(res.data.tagihan_popup_pesan || '')
  }

  async function simpan(overrides) {
    setError(''); setSaving(true); setSaved(false)
    try {
      await api.post('/isolir-pengaturan', {
        isolir_network_enabled: networkEnabled, isolir_bandwidth_limit: bandwidthLimit,
        tagihan_popup_interval_jam: popupIntervalJam, tagihan_popup_judul: popupJudul,
        tagihan_popup_pesan: popupPesan, ...overrides
      })
      await load()
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan pengaturan isolir otomatis')
    } finally { setSaving(false) }
  }

  return (
    <Card style={{ padding: 22 }}>
      <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ShieldAlert size={16} color="var(--brand)" /> Isolir Otomatis
      </h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 16 }}>
        Pelanggan yang lewat jatuh tempo, belum bayar sama sekali, dan belum dikonfirmasi keterlambatan (lihat tombol di halaman Tagihan) akan diisolir otomatis. Efek jaringan (limit bandwidth + redirect ke halaman tagihan, terpisah dari sistem RADIUS lama) baru aktif kalau toggle di bawah dinyalakan.
      </p>
      {error && <Alert type="error">{error}</Alert>}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 16 }}>
        <input type="checkbox" checked={networkEnabled} onChange={e => { setNetworkEnabled(e.target.checked); simpan({ isolir_network_enabled: e.target.checked }) }} />
        Aktifkan efek jaringan (limit bandwidth + captive portal) ke Mikrotik
      </label>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Limit bandwidth isolir" style={{ flex: '0 1 160px' }}>
          <Input value={bandwidthLimit} onChange={e => setBandwidthLimit(e.target.value)} placeholder="1M" />
        </Field>
        <Field label="Interval popup notice tagihan (jam)" style={{ flex: '0 1 220px' }}>
          <Input type="number" min={1} max={168} value={popupIntervalJam} onChange={e => setPopupIntervalJam(e.target.value)} placeholder="6" />
        </Field>
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Judul popup notice (halaman Tagihan Publik pelanggan isolir)">
          <Input value={popupJudul} onChange={e => setPopupJudul(e.target.value)} placeholder="Tagihan Anda belum terbayar" />
        </Field>
        <Field label="Isi pesan popup notice">
          <Textarea value={popupPesan} onChange={e => setPopupPesan(e.target.value)} rows={4} style={{ width: '100%' }}
            placeholder="Mohon segera lakukan pembayaran agar layanan internet Anda tetap berjalan lancar..." />
        </Field>
      </div>

      <Button icon={saved ? Check : Save} onClick={() => simpan()} loading={saving} style={{ marginTop: 16 }}>{saved ? 'Tersimpan' : 'Simpan'}</Button>
    </Card>
  )
}

// Pengaturan GenieACS (2026-07-24, audit modularitas) -- URL GenieACS (NBI/API), URL CWMP (TR-069,
// ditulis ke tiap ONT saat provisioning "Sinkron ke GenieACS"), dan VLAN TR-069 dulu 100% env var +
// redeploy, sekarang lewat panel ini (app_config, lihat server/genieacsConfig.js). Kosongkan field
// utk kembali ke default env var, sama pola dgn panel "Koneksi Mikrotik Produksi".
function GenieAcsConfigCard() {
  const [url, setUrl] = useState('')
  const [cwmpUrl, setCwmpUrl] = useState('')
  const [vlan, setVlan] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    const res = await api.get('/genieacs/pengaturan')
    setUrl(res.data.url || ''); setCwmpUrl(res.data.cwmpUrl || ''); setVlan(res.data.vlan || '')
  }

  async function simpan() {
    setError(''); setSaving(true); setSaved(false)
    try {
      const res = await api.post('/genieacs/pengaturan', { url, cwmpUrl, vlan })
      setUrl(res.data.url || ''); setCwmpUrl(res.data.cwmpUrl || ''); setVlan(res.data.vlan || '')
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan pengaturan GenieACS')
    } finally { setSaving(false) }
  }

  return (
    <Card style={{ padding: 22 }}>
      <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Server size={16} color="var(--brand)" /> Pengaturan GenieACS
      </h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 16 }}>
        Alamat server GenieACS yang dipakai fitur Perangkat Pelanggan, Diagnostik, dan provisioning ONT ("Sinkron ke GenieACS"). Kosongkan field untuk kembali ke nilai default.
      </p>
      {error && <Alert type="error">{error}</Alert>}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <Field label="URL GenieACS (NBI/API)" style={{ flex: '1 1 220px' }}>
          <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="http://127.0.0.1:7557" />
        </Field>
        <Field label="URL CWMP (TR-069)" style={{ flex: '1 1 220px' }}>
          <Input value={cwmpUrl} onChange={e => setCwmpUrl(e.target.value)} placeholder="http://10.10.10.254:7547" />
        </Field>
        <Field label="VLAN TR-069" style={{ flex: '0 1 130px' }}>
          <Input value={vlan} onChange={e => setVlan(e.target.value)} placeholder="1011" />
        </Field>
      </div>
      <Button icon={saved ? Check : Save} onClick={simpan} loading={saving}>{saved ? 'Tersimpan' : 'Simpan'}</Button>
    </Card>
  )
}

// Ambang ukuran folder foto (2026-07-24, audit modularitas) -- dulu env var FOTO_SIZE_WARNING_MB +
// redeploy, sekarang app_config (server/healthCheck.js's getFotoSizeWarningMb/setFotoSizeWarningMb).
// Dipakai kartu "Ukuran Foto" di Dashboard & alert kesehatan sistem otomatis (cron 10 menit).
function FotoSizeThresholdCard() {
  const [nilai, setNilai] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    const res = await api.get('/health/pengaturan')
    setNilai(String(res.data.fotoSizeWarningThresholdMb ?? ''))
  }

  async function simpan() {
    setError(''); setSaving(true); setSaved(false)
    try {
      const res = await api.post('/health/pengaturan', { fotoSizeWarningThresholdMb: nilai })
      setNilai(String(res.data.fotoSizeWarningThresholdMb ?? ''))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan ambang ukuran folder foto')
    } finally { setSaving(false) }
  }

  return (
    <Card style={{ padding: 22 }}>
      <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <HardDrive size={16} color="var(--brand)" /> Ambang Ukuran Folder Foto
      </h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 16 }}>
        Kartu "Ukuran Foto" di Dashboard dan alert WA kesehatan sistem akan memperingatkan begitu folder foto bukti pekerjaan melewati batas ini.
      </p>
      {error && <Alert type="error">{error}</Alert>}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end' }}>
        <Field label="Batas ukuran (MB)" style={{ flex: '0 1 160px' }}>
          <Input type="number" min={10} value={nilai} onChange={e => setNilai(e.target.value)} placeholder="500" />
        </Field>
        <Button icon={saved ? Check : Save} onClick={simpan} loading={saving}>{saved ? 'Tersimpan' : 'Simpan'}</Button>
      </div>
    </Card>
  )
}

// Aturan Pegawai (Fitur Payroll, 2026-07-21) -- teks bebas berlaku semua pegawai (target kerja
// per-pegawai sendiri ada di form Edit Pegawai, ini cuma utk aturan umum spt jam kerja/SOP), tersimpan
// di app_config key 'aturan_pegawai' lewat GET/PUT /api/pegawai/aturan/text.
function AturanPegawaiCard() {
  const [aturan, setAturan] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => { api.get('/pegawai/aturan/text').then(r => setAturan(r.data.aturan)) }, [])

  async function simpan() {
    setSaving(true); setMsg(null)
    try {
      await api.put('/pegawai/aturan/text', { aturan })
      setMsg({ type: 'success', text: 'Tersimpan' })
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Gagal menyimpan' })
    } finally { setSaving(false) }
  }

  return (
    <Card style={{ padding: 22 }}>
      <h2 style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <ClipboardCheck size={16} color="var(--brand)" /> Aturan Pegawai
      </h2>
      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 14 }}>
        Aturan umum yang berlaku semua pegawai (jam kerja, SOP, dll). Target jumlah pekerjaan per bulan diatur per-pegawai lewat halaman Pegawai.
      </p>
      <Textarea value={aturan} onChange={e => setAturan(e.target.value)} rows={6} placeholder="mis. Jam kerja 08:00-17:00, wajib lapor via WA grup tiap selesai pekerjaan..." style={{ width: '100%', minHeight: 120 }} />
      {msg && <div style={{ marginTop: 10 }}><Alert type={msg.type}>{msg.text}</Alert></div>}
      <Button style={{ marginTop: 12 }} loading={saving} onClick={simpan}>Simpan</Button>
    </Card>
  )
}

function CleanupConfigCard() {
  const [cfg, setCfg] = useState(null)
  const [log, setLog] = useState([])
  const [showLog, setShowLog] = useState(false)
  const [running, setRunning] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => { load() }, [])

  async function load() {
    try {
      const res = await api.get('/genieacs/cleanup/config')
      setCfg(res.data)
    } catch { /* diamkan, bukan fitur utama halaman ini */ }
  }

  async function save(patch) {
    const next = { ...cfg, ...patch }
    setCfg(next)
    try {
      const res = await api.post('/genieacs/cleanup/config', { enabled: next.enabled, days: next.days })
      setCfg(res.data)
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Gagal menyimpan pengaturan' })
      load()
    }
  }

  async function runNow() {
    setRunning(true); setMsg(null)
    try {
      const res = await api.post('/genieacs/cleanup/run')
      setMsg({ type: 'success', text: res.data.message })
      load()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Gagal menjalankan cleanup' })
    } finally { setRunning(false) }
  }

  async function loadLog() {
    setShowLog(true)
    try {
      const res = await api.get('/genieacs/cleanup/log')
      setLog(res.data)
    } catch { setLog([]) }
  }

  if (!cfg) return null

  return (
    <Card style={{ padding: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Trash2 size={16} color="var(--brand)" />Auto-Hapus Device Offline Lama
        </h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={!!cfg.enabled} onChange={() => save({ enabled: !cfg.enabled })} />
          Aktifkan
        </label>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginBottom: 14 }}>
        Device yang tidak pernah check-in ke GenieACS selama batas hari di bawah akan dihapus otomatis setiap hari jam 03:00 WIB.
        Kalau device-nya dipakai lagi nanti, dia akan otomatis terdaftar ulang begitu online kembali — jadi aman dihapus.
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
        <Field label="Batas hari offline">
          <Input type="number" min={7} style={{ width: 120 }} value={cfg.days}
            onChange={e => setCfg({ ...cfg, days: parseInt(e.target.value) || 0 })}
            onBlur={() => cfg.days >= 7 && save({ days: cfg.days })} />
        </Field>
        <Button variant="soft" icon={PlayCircle} loading={running} onClick={runNow}>Jalankan Sekarang</Button>
        <Button variant="ghost" size="sm" onClick={loadLog}>Lihat Riwayat</Button>
      </div>

      {cfg.lastRunAt && (
        <p style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 10 }}>
          Terakhir jalan: {new Date(cfg.lastRunAt).toLocaleString('id-ID')} — {cfg.lastRunDeleted} device dihapus.
        </p>
      )}

      {msg && <div style={{ marginTop: 10 }}><Alert type={msg.type}>{msg.text}</Alert></div>}

      {showLog && (
        <Modal onClose={() => setShowLog(false)} width={640}>
          <ModalHeader title="Riwayat Auto-Hapus" onClose={() => setShowLog(false)} />
          {log.length === 0 ? (
            <EmptyState icon={Inbox} title="Belum ada device yang terhapus otomatis" />
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Serial Number', 'Model', 'No. Pelanggan', 'Terakhir Online', 'Dihapus Pada'].map(h => (
                    <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {log.map(l => (
                  <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px' }}>{l.serial_number || '-'}</td>
                    <td style={{ padding: '7px 10px' }}>{l.model || '-'}</td>
                    <td style={{ padding: '7px 10px' }}>{l.ppp_username || '-'}</td>
                    <td style={{ padding: '7px 10px' }}>{l.last_inform ? new Date(l.last_inform).toLocaleString('id-ID') : '-'}</td>
                    <td style={{ padding: '7px 10px' }}>{new Date(l.deleted_at).toLocaleString('id-ID')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </Card>
  )
}
